import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { IContract, IPaginationParams } from "types";
import { Loader } from "pages/Loader/Loader";
import { ListItem } from "../ListItem/ListItem";

interface Props {
  items: IContract[];
  hasNextPage: boolean;
  loadNextPage: (params: IPaginationParams) => Promise<any>;
  pagination: IPaginationParams;
}

export const ContractsList: FC<Props> = ({
  hasNextPage,
  pagination: { page, pageSize },
  items,
  loadNextPage,
}) => {
  const [active, setActive] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  // Для виртуализации
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleEndIndex, setVisibleEndIndex] = useState(20); // Начальное количество видимых элементов
  const ITEM_HEIGHT = 76; // Фиксированная высота элемента (при свернутом состоянии)
  const BUFFER = 5; // Буфер элементов сверху и снизу

  // Обработчик загрузки следующей страницы
  const loadMoreItems = useCallback(async () => {
    if (!loading && hasNextPage) {
      setLoading(true);
      try {
        await loadNextPage({
          page: page + 1,
          pageSize,
        });
      } finally {
        setLoading(false);
      }
    }
  }, [loading, hasNextPage, loadNextPage, page, pageSize]);

  // IntersectionObserver для подгрузки новых элементов
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            loadMoreItems();
          }
        },
        {
          root: containerRef.current,
          threshold: 1.0,
        }
      );
      if (node) observerRef.current.observe(node);
    },
    [loading, hasNextPage, loadMoreItems]
  );

  
  const onScroll = () => {
    if (!containerRef.current) return;
    const scrollTop = containerRef.current.scrollTop;
    const containerHeight = containerRef.current.clientHeight;
    const startIndex = Math.floor(scrollTop / ITEM_HEIGHT) - BUFFER;
    const endIndex = Math.ceil((scrollTop + containerHeight) / ITEM_HEIGHT) + BUFFER;
    setVisibleStartIndex(Math.max(0, startIndex));
    setVisibleEndIndex(Math.min(items.length, endIndex));
  };

  useEffect(() => {
    
    if (active === undefined && containerRef.current) {
      containerRef.current.addEventListener("scroll", onScroll);
      onScroll();
      return () => {
        containerRef.current?.removeEventListener("scroll", onScroll);
      };
    }
  }, [items.length, active]);

  useEffect(() => {
    setActive(undefined);
  }, [items]);

 
  const useVirtualization = active === undefined;
  const totalHeight = items.length * ITEM_HEIGHT;

  // Добавляем функцию для измерения и обновления высоты
  const measureAndUpdate = useCallback(() => {
    // Обновляем состояние списка после изменения высоты элемента
    if (containerRef.current) {
      // Форсируем перерасчет видимой области
      onScroll();
    }
  }, [onScroll]);

  return (
    <div
      ref={containerRef}
      style={{
        height: "70vh", 
        overflowY: "auto",
        position: "relative",
      }}
    >
      {useVirtualization ? (
        <div style={{ height: totalHeight, position: "relative" }}>
          {items.slice(visibleStartIndex, visibleEndIndex).map((contract, index) => {
            const actualIndex = visibleStartIndex + index;
            return (
              <div
                key={contract.idContract}
                style={{
                  position: "absolute",
                  top: actualIndex * ITEM_HEIGHT,
                  left: 0,
                  right: 0,
                  height: ITEM_HEIGHT,
                }}
              >
                <div style={{ position: "relative", marginBottom: active === contract.idContract ? "16px" : "0" }}>
                  <ListItem
                    contract={contract}
                    active={active === contract.idContract}
                    setActive={setActive}
                    measure={measureAndUpdate}
                  />
                </div>
              </div>
            );
          })}
          {hasNextPage && (
            <div
              ref={loadMoreRef}
              style={{
                position: "absolute",
                top: totalHeight,
                left: 0,
                right: 0,
              }}
            >
              <Loader />
            </div>
          )}
        </div>
      ) : (
        
        <>
          {items.map((contract) => (
            <div key={contract.idContract} style={{ position: "relative", marginBottom: active === contract.idContract ? "16px" : "0" }}>
              <ListItem
                contract={contract}
                active={active === contract.idContract}
                setActive={setActive}
                measure={measureAndUpdate}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
